<template>
  <div>
    <div class="comments-wrapper" v-if="isChatActive">
      <div class="comments-button" @click="toggleChat">
        <span>{{ $t('GeneralTranslations.Messages') }}</span>
        <v-icon class="pl-1" size="12px" color="white">mdi-triangle-down</v-icon>
      </div>
      <v-card class="chat-box">
        <v-row class="pt-4 px-4" justify="space-around d-flex flex-column">
          <div class="v-list-scrollable" ref="scrollableList">
            <Loader v-if="loading" />
            <v-list v-else>
              <template v-for="(message) in messages">
                <v-list-item v-if="message.user.id != user.id" :key="message.createdAt">
                  <v-list-item-avatar class="align-self-start mr-2">
                    <v-avatar size="40">
                      <v-img :src="message.user.profileImage"></v-img>
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content class="received-message">
                    <v-card color="success" class="flex-none" style="box-shadow: none !important">
                      <v-card-text class="white--text pa-2 d-flex flex-column">
                        <span class="text-caption">{{ message.user.firstName }} </span>
                        <span class="align-self-start text-subtitle-1">{{ message.text }}</span>
                        <span class="text-caption font-italic align-self-end">{{ message.createdAtDisplay }}</span>
                        <ChatMedia v-if="message?.medias?.length > 0" :data="{ medias: message.medias }"></ChatMedia>
                      </v-card-text>
                    </v-card>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-else :key="message.createdAt">
                  <v-list-item-content class="sent-message justify-end">
                    <v-card color="primary" class="flex-none no-shadow" style="box-shadow: none !important">
                      <v-card-text class="white--text pa-2 d-flex flex-column">
                        <span class="text-subtitle-1 chat-message">{{ message.text }}</span>
                        <span class="text-caption font-italic align-self-start">{{ message.createdAtDisplay }}</span>
                        <ChatMedia v-if="message?.medias?.length > 0" :data="{ medias: message.medias }"></ChatMedia>
                      </v-card-text>
                    </v-card>
                  </v-list-item-content>
                  <v-list-item-avatar class="align-self-start ml-2">
                    <v-img :src="user.profilePicUrl"></v-img>
                  </v-list-item-avatar>
                </v-list-item>
              </template>
            </v-list>

            <div ref="bottomElement"></div>
          </div>
        </v-row>

        <div class="messages-form p-4">
          <v-textarea v-model="message" :disabled="isFileUploading || loading" v-on:keyup.enter="sendMessage(null)"
            :label="'Enter your message here to reply'" rows="1"></v-textarea>

          <v-btn width="100px" :disabled="!message?.length || isMessageInvalid" :loading="isFileUploading || isSending || loading"
            class="btn-default inline-block float-right" @click="sendMessage(null)">
            {{ $t('GeneralTranslations.Send') }}
          </v-btn>

          <div class="float-left">
            <ChatMediaUploader :filePath="filePath" @onFileStartUpload="onFileStartUpload"
              @onFileUploaded="onFileUploaded" @onImageSelected="onImageSelected"></ChatMediaUploader>
          </div>
        </div>
      </v-card>
    </div>
    <div class="open-comments-button" v-else @click="toggleChat">
      <span>{{ $t('GeneralTranslations.Messages') }}</span>
      <v-badge v-if="unreadFamilyMessagesCount > 0" class="v-badge-comments" :content="unreadFamilyMessagesCount"
        color="red" overlap>
      </v-badge>
    </div>
  </div>
</template>

<script>
import moment from "moment/moment";
import * as firebase from "firebase/app";
import { mapGetters } from "vuex";
import { CommentSenderRoleEnum } from '@/enums/CommentSenderRoleEnum';
import { v4 as uuidv4 } from "uuid";
import ChatMedia from '@/components/chat/ChatMedia.vue'
import ChatMediaUploader from '@/components/chat/ChatMediaUploader.vue'
import { nextTick } from 'vue';
import Loader from '@/components/loader/Loader.vue';
export default {
  name: "Chat",
  components: { ChatMedia, ChatMediaUploader, Loader },
  props: {
    data: Object
  },
  data() {
    return {
      loading: true,
      messages: [],
      message: null,
      onMessagesUnsubscribe: null,
      isChatActive: false,
      isFileUploading: false,
      isSending: false
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    dateTimeFormat() {
      return `${this.facility.dateFormat} ${this.facility.timeFormat}`
    },
    channel() {
      return `${this.user.id}${this.data.familyMember.id}`
    },
    filePath() {
      return `${this.facility.id}/channel_chat_images/${this.data.familyMember.id}/${this.user.id}`;
    },
    unreadFamilyMessagesCount(){
      return this.messages?.filter(x=>!x.read)?.length || 0
    },
    isMessageInvalid() {
      return (!this.message || this.message.trim() === "") && !this.isUploadingFile && !this.media;
    },
    ...mapGetters({
      user: "user"
    })
  },
  mounted() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          this.scrollToLastItem();
          console.log('SCROLL')
        }
      },
      { threshold: 0.5 }
    );

    if (this.$refs.bottomElement) {
      observer.observe(this.$refs.bottomElement);
      console.log('SCROLL')
    }
  },

  beforeDestroy() {
    this.unsubscribeFromMessages()
    this.closeChat();
  },

  async created(){
    await this.initChat()
  },

  methods: {
    async toggleChat() {
      this.isChatActive = !this.isChatActive;

      if (this.isChatActive) {
        await this.readIncomingMessages(this.data.familyMember.id)
        await nextTick()
        this.scrollToLastItem()
      }
      else {
        this.closeChat()
      }
    },

    async initChat() {
      await this.fetchMessages()
      await this.subscribeOnMessages()
      this.loading = false
    },

    closeChat() {
      console.log('closeChat')
      this.loading = false
      this.message = null
      this.isChatActive = false
      this.isFileUploading = false
    },

    async sendMessage(media = null) {

      if (this.isMessageInvalid && !media) return; 

      this.isSending = true
      const newMessage = {
        id: uuidv4(),
        to: CommentSenderRoleEnum.Family,
        from: CommentSenderRoleEnum.Staff,
        channel: this.channel,
        createdAt: new Date().toISOString(),
        staffId: this.user.id,
        familyHubUserId: this.data.familyMember.id,
        read: false,
        readDateTime: null,
        text: this.message,
        facilityId: this.facility.id,
        orgId: this.facility.orgId,
        medias: [],
        user: {
          id: this.user.id,
          firstName: this.user.name,
          lastName: null,
          profileImage: this.user.thumbnailUrl,
          customProperties: null
        }
      }

      if (media) {
        newMessage.medias = [media]
      }

      this.message = ''

      await this.$store.dispatch("createMessage", newMessage);
      this.scrollToLastItem()
      this.isSending = false
    },

    async fetchMessages() {
      this.messages = await this.getMessages()
      this.scrollToLastItem()
      console.log(`fetched ${this.messages.length} messages`)
    },

    async editMessage({ messageId, newContent, roomId, files }) {
      console.log('editMessage')
    },

    async deleteMessages({ message, roomId }) {
      console.log('deleteMessage')
    },

    onFileStartUpload() {
      this.isFileUploading = true;
      console.log('onFileStartUpload');
    },

    async onFileUploaded(file) {
      if (!file?.url) {
        throw Error('Unable to upload file')
      }

      const media = {
        type: file.type,
        url: file.url
      }

      await this.sendMessage(media)
      this.isFileUploading = false
    },

    async onImageSelected(image) {

      if (!image?.image) {
        throw Error('Unable to select image')
      }

      const media = {
        type: 'image',
        url: image.image
      }

      await this.sendMessage(media)
    },

    async readIncomingMessages() {
      const result = await this.$store.dispatch("readIncomingFamilyMessages", this.channel)
      if (result === true && this.messages) {
        this.messages.forEach(item => {
          if (item.senderRole !== CommentSenderRoleEnum.Staff) {
            item.read = true
          }
        });
      }
    },

    async readIncomingMessage(message) {
      if (message.to === CommentSenderRoleEnum.Staff) return

      const result = await this.$store.dispatch("readIncomingFamilyMessage", message.id)
      if (result === true) {
        const item = this.messages.find(x => x.id === message.id);
        item.read = true
      }
    },

    async getMessages() {

      const today = moment();
      return await firebase
        .firestore()
        .collection('messages')
        .where('facilityId', '==', this.facility.id)
        .where('channel', '==', this.channel)
        .orderBy('createdAt', 'asc')
        .get()
        .then(qs => {
          return qs.docs
            .map(event => {
              const message = { id: event.id, ...event.data() };
              const dateTime = moment(message.createdAt);
              message.createdAtDisplay = dateTime.isSame(today, 'day')
                ? dateTime.format(this.facility.timeFormat)
                : dateTime.format(this.dateTimeFormat);

              return message;
            })
        })
        .catch(e => {
          console.error(e);
          return []
        });
    },

    async subscribeOnMessages() {
      console.log('subscribeOnMessages')

      const messagesQuery = firebase.firestore().collection("messages")
        .where('channel', '==', this.channel)
        .where("facilityId", "==", this.facility.id)
        // request data that was created after start listening
        .where("createdAt", ">=", new Date().toISOString())
        .orderBy("createdAt", "desc")

      this.onMessagesUnsubscribe = await messagesQuery
        .onSnapshot((snapshot) => {

          snapshot.docChanges().forEach((change) => {

            if (change.type === "added") {
              console.log(`received new message`)
              const message = [{ id: change.doc.id, ...change.doc.data() }][0]
              this.messages.push(message)
              this.scrollToLastItem()
              this.readIncomingMessage(message)
            }

            if (change.type === "removed") {
              console.log(`interlocutor removed a message`)
              this.messages = this.messages.filter(x => x.id !== change.doc.id && x.id !== change.doc.data().id)
            }
          })
        })
    },

    unsubscribeFromMessages() {
      console.log('unsubscribeFromMessages')
      if (this.onMessagesUnsubscribe) {
        this.onMessagesUnsubscribe()
      }
    },

    scrollToLastItem() {
      this.$nextTick(() => {
        setTimeout(() => {
          const bottomElement = this.$refs.bottomElement;
          if (bottomElement) {
            bottomElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }, 500);
      });
    }
  }
}
</script>

<style scoped lang="scss">
.v-card {
  padding: initial !important;
  min-height: initial !important;
  background-color: #fff !important;
}

.messages-form {
  bottom: 0;
  width: -webkit-fill-available;
  margin-top: auto;
}

.no-shadow {
  box-shadow: none !important;
}

.chat-message {
  display: unset !important;
  white-space: break-spaces;
}

.chat-screen {
  max-height: 320px;
  overflow-y: auto;
}

.flex-none {
  flex: unset;
}

.received-message,
.sent-message {
  line-break: anywhere;
  cursor: text;
}

.received-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: 54px;
  right: auto;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #4caf50 transparent transparent transparent;
}

.sent-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: 54px;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #3f51b5 transparent transparent transparent;
}

.comments-button {
  position: fixed;
  bottom: 750px;
  right: 430px;
  width: 140px;
  height: 50px;
  background-color: #3f51b5;
  transform-origin: center top;
  transform: rotate(-90deg);
  color: white;
  text-align: center;
  line-height: 50px;
  transition: right 0.25s;
  cursor: pointer;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  outline: none;
}

@media screen and (max-width: 800px) {
  .comments-button {
    position: static;
    width: 100%;
    max-width: 350px;
    margin: auto;
    height: 50px;
    transform: rotate(0deg);
  }
}

.chat-box {
  width: 450px;
  height: 870px;
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.chat-content {
  list-style: none;
  padding: 0;
  color: #fff;
}

.v-list-scrollable {
  max-height: 740px;
  overflow-y: auto !important;
  flex-grow: 1;
}

.v-list-scrollable::-webkit-scrollbar {
  width: 15px;
}

.v-list-scrollable::-webkit-scrollbar-track {
  background: #e6e6e6;
  border-left: 1px solid #dadada;
}

.v-list-scrollable::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border: solid 3px #e6e6e6;
  border-radius: 7px;
}

.v-list-scrollable::-webkit-scrollbar-thumb:hover {
  background: rgb(117, 117, 163);
}

.open-comments-button {
  bottom: 600px;
  position: fixed;
  right: 0px;
  width: 100px;
  height: 60px;
  background-color: #3f51b5;
  transform-origin: center top;
  transform: rotate(-90deg);
  color: white;
  text-align: center;
  line-height: 50px;
  transition: right 0.25s;
  cursor: pointer;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  outline: none;
}

.open-comments-button:hover,
.open-comments-button:focus {
  right: 10px
}

.open-comments-button.active+.chat-box {
  right: 0;
}

@media screen and (max-width: 800px) {
  .open-comments-button {
    position: static;
    width: 100%;
    max-width: 350px;
    margin: auto;
    height: 50px;
    transform: rotate(0deg);
  }
}

.v-badge.v-badge-comments {
  transform: rotate(90deg);
  position: absolute;
  margin-left: 10px;
}
</style>
